/**
 * ChatServer — Facade service for all server_chat communication
 *
 * Single entry point for both REST API calls and Socket.IO events (/chat namespace).
 * Vue components ONLY call ChatServer.* — no raw sockets or fetch calls in component code.
 *
 * Encryption: Option B (Shared Conversation Key AES-256-GCM)
 * The conversation's AES key is stored locally in ChatDatabaseService (IndexedDB).
 * All encryption/decryption happens client-side before send and after receive.
 */

import { io, Socket } from 'socket.io-client'
import { ChatDatabaseService, type DecryptedMessage, type ChatConversation, type DeviceKey } from './ChatDatabaseService'

// '' (empty string) = same-origin: requests go through the Vite/ingress proxy.
// Required for Safari — WebKit blocks ws:// from an https page even for localhost.
const CHAT_API  = import.meta.env.VITE_CHAT_API_URL  ?? 'http://localhost:8096'
const CHAT_WS   = import.meta.env.VITE_CHAT_WS_URL   ?? 'http://localhost:8096'
const CHAT_WS_PATH = import.meta.env.VITE_CHAT_WS_PATH || '/chat-ws'

// ── Types ─────────────────────────────────────────────────────────────────────
export interface NewConversationParams {
  type: 'private' | 'group' | 'studio_channel' | 'anonymous'
  name?: string
  participantIds: string[]
  companyId: string
  /** Pre-built per-device encrypted key envelopes (built by caller using ChatDatabaseService.unwrapConversationKey) */
  encryptedKeys: Array<{ deviceId: string; userId: string; encrypted_key: string; iv: string; tag: string }>
  options?: Record<string, any>
}

export interface SendMessageParams {
  conversationId: string
  plaintext: string
  aesKey: CryptoKey
  content_type?: string
  ref_type?: string | null
  ref_id?: string | null
  ref_label?: string | null
  ref_data?: any | null
  metadata?: Record<string, any> | null
}

export interface TypingEvent {
  conversationId: string
  userid: string
  isTyping: boolean
}

export interface PresenceEvent {
  userid: string
  online: boolean
}

export interface ReadReceipt {
  conversationId: string
  userid: string
  messageId: string
  readAt: string
}

// ── Internal state ────────────────────────────────────────────────────────────
let socket: Socket | null = null
let currentJwt: string   = ''

// Conversation AES key cache (in-memory, per session — fetched once per conversation)
const aesKeyCache = new Map<string, CryptoKey>()

// ── Auth header helper ────────────────────────────────────────────────────────

/** Decode JWT payload without verifying signature (client-side check only) */
function isTokenExpired (jwt: string): boolean {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]))
    // exp is Unix seconds; give 30s buffer so we refresh before the server rejects
    return payload.exp && (payload.exp - 30) < (Date.now() / 1000)
  } catch {
    return false  // unknown format — let the server decide
  }
}

/** Wait for the studio server.worker to refresh the access token.
 *  Sends a 'refreshtoken' request via BroadcastChannel and waits for the
 *  'tokenrefreshed' reply (max 12 s). Falls back to IDB polling if no reply.
 */
async function waitForTokenRefresh (): Promise<string> {
  const stale = currentJwt

  return new Promise<string>((resolve) => {
    // Open the same channel the worker listens on
    const ch = new BroadcastChannel('studio_auth')
    let settled = false

    const timeout = setTimeout(async () => {
      if (settled) return
      settled = true
      ch.close()
      // Fallback: read whatever is in IDB (might still be stale)
      const fb = await ChatDatabaseService.getStudioToken().catch(() => stale)
      resolve(fb || stale)
    }, 12_000)

    ch.onmessage = async (ev: MessageEvent) => {
      if (settled) return
      if (ev.data?.cmd === 'tokenrefreshed' && ev.data?.at && ev.data.at !== stale) {
        settled = true
        clearTimeout(timeout)
        ch.close()
        resolve(ev.data.at)
      }
    }

    // Ask the worker to refresh
    ch.postMessage({ cmd: 'refreshtoken' })
  })
}

/** Only a non-empty plain string is a usable JWT — anything else (e.g. an
 *  EncryptedBlob from a raw IDB read) must never be sent as a Bearer token. */
function asJwt (value: unknown): string {
  return typeof value === 'string' ? value : ''
}

async function getAuthHeader (): Promise<Record<string, string>> {
  // Always read the freshest JWT from IndexedDB — the studio server worker renews it
  // continuously, so this is always valid even after the 15-min token lifetime.
  const fresh = asJwt(await ChatDatabaseService.getStudioToken().catch(() => null))
  const jwt = fresh || currentJwt
  if (fresh) currentJwt = fresh  // keep local copy in sync
  return jwt ? { Authorization: `Bearer ${jwt}` } : {}
}

async function apiFetch (path: string, options: RequestInit = {}, _retry = true): Promise<any> {
  let headers = await getAuthHeader()

  // Proactively refresh if the token we have is already within 30s of expiry
  const tokenVal = headers.Authorization?.slice(7) ?? ''
  if (tokenVal && isTokenExpired(tokenVal)) {
    console.warn('[ChatServer] token near expiry — refreshing before request')
    const refreshed = await waitForTokenRefresh()
    if (refreshed) {
      currentJwt = refreshed
      headers = { Authorization: `Bearer ${refreshed}` }
    }
  }

  const res = await fetch(`${CHAT_API}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...headers, ...(options.headers || {}) }
  })

  // On 401, ask the studio worker to renew the token and retry once
  if (res.status === 401 && _retry) {
    console.warn('[ChatServer] 401 received — waiting for token refresh then retrying')
    const newToken = await waitForTokenRefresh()
    if (newToken) currentJwt = newToken
    return apiFetch(path, options, false)  // false = no second retry
  }

  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return res.json()
}

/**
 * Run a request/response over the Socket.IO connection (ack callback), preferring
 * the live socket and falling back to HTTP when the socket is not connected yet
 * (cold mount / offline). Reading chat data over the socket avoids the per-message
 * HTTP polling that previously hit /api/chat/* on every incoming message.
 */
function socketRequest<T = any> (event: string, payload: any, httpFallback: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    // Socket not established (still handshaking, offline, or disconnected) → use HTTP
    if (!socket || !socket.connected) {
      httpFallback().then(resolve, reject)
      return
    }
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      console.warn(`[ChatServer] socket "${event}" timed out — falling back to HTTP`)
      httpFallback().then(resolve, reject)
    }, 8000)

    socket.emit(event, payload, (res: any) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (res?.ok) resolve(res as T)
      else reject(new Error(res?.error || `socket ${event} failed`))
    })
  })
}

export class ChatServer {
  // ── Connection ──────────────────────────────────────────────────────────────
  static connect (jwt: string, companies: string[] = []): void {
    currentJwt = jwt   // always update — REST calls (apiFetch) use this as fallback

    // If already connected OR still connecting, don't create a second socket.
    // socket?.connected guards "fully established"; socket !== null guards "still handshaking".
    // Without the null check, navigating quickly (CommunicationOverview → ChatView) creates
    // a second io() while the first handshake is in flight, abandoning it mid-connection
    // and producing "WebSocket closed before connection established".
    if (socket !== null) return

    socket = io(`${CHAT_WS}/chat`, {
      path: CHAT_WS_PATH,
      transports: ['websocket'],
      // Callable auth: invoked on every connect/reconnect attempt so the JWT is always fresh
      auth: async (cb: (data: object) => void) => {
        const fresh = asJwt(await ChatDatabaseService.getStudioToken().catch(() => null))
        let token = fresh || currentJwt
        // Beim Kaltstart steht in IndexedDB noch der Token der letzten Sitzung.
        // server_chat prueft mit jwt.verify — abgelaufen ist dort AUTH_INVALID,
        // und socket.io wiederholt den Versuch alle 2 s mit genau demselben
        // toten Token, bis der Studio-Worker irgendwann einen frischen in die
        // IDB schreibt. apiFetch prueft die Ablaufzeit laengst vor dem Absenden;
        // der Socket tat es als einziger Pfad nicht.
        if (token && isTokenExpired(token)) {
          console.warn('[ChatServer] stored token expired — refreshing before connect')
          const refreshed = await waitForTokenRefresh()
          if (refreshed) token = refreshed
        }
        if (token) currentJwt = token
        cb({ token, companies: JSON.stringify(companies) })
      },
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: Infinity
    })

    socket.on('connect',       () => console.log('[ChatServer] connected'))
    socket.on('disconnect', (reason) => console.log('[ChatServer] disconnected', reason))
    socket.on('connect_error', (err)  => console.error('[ChatServer] auth error', err.message))

    // Hakt eine Kollegin eine Anfrage ab, muss der Header-Badge das sofort
    // zeigen — nicht erst beim 60-s-Vollabzug des Stores.
    //
    // Bewusst hier und nicht über `onConversationStatus()`: das räumt bei jeder
    // Registrierung ALLE Listener des Ereignisses ab (`socket.off` ohne
    // Handler), damit ein Remount der Chat-Ansicht keine Dubletten anhäuft. Ein
    // zweiter Zuhörer daneben würde davon mitgerissen — dieselbe Falle, die
    // beim `chat:message:new`-Listener des Stores schon dokumentiert ist.
    // In `connect()` hängt der Listener dagegen am Socket selbst, lebt so lange
    // wie er und übersteht jedes Mount/Unmount.
    //
    // Weitergereicht wird als Fensterereignis: denselben Weg nimmt der
    // Statuswechsel am eigenen Gerät bereits (siehe `setStatus`), der Store
    // hat dafür also schon einen Zuhörer. Ein Fensterereignis erreicht zudem
    // beliebig viele Empfänger — anders als der Socket-Listener oben.
    socket.on('chat:conversation:status', (d: any) => {
      if (!d?.conversationId) return
      window.dispatchEvent(new CustomEvent('chat:unread-changed', {
        detail: { conversationId: d.conversationId, status: d.status ?? null }
      }))
    })
  }

  static disconnect (): void {
    socket?.disconnect()
    socket = null
    aesKeyCache.clear()
  }

  static onConnected (cb: () => void): void {
    socket?.on('connect', cb)
    if (socket?.connected) cb()
  }

  // ── Conversations ────────────────────────────────────────────────────────────
  private static lastSyncAt: string | null = null

  /** Initial or delta load of conversations.
   *  First call: full load (no ?since). Subsequent calls: delta via ?since=lastSyncAt.
   *  Mirrors the server.worker.ts pattern: initial sync then incremental updates. */
  static async fetchConversations (forceFull = false): Promise<ChatConversation[]> {
    const since = (!forceFull && this.lastSyncAt) ? this.lastSyncAt : null

    // Prefer the live socket; fall back to HTTP only when the socket isn't connected.
    const data = await socketRequest('chat:conversations:fetch', { since }, () => {
      const url = since
        ? `/api/chat/conversations?since=${encodeURIComponent(since)}`
        : '/api/chat/conversations'
      return apiFetch(url)
    })
    const incoming: ChatConversation[] = data.conversations || []

    // Remember when we synced — server sends back its own timestamp to avoid clock skew
    if (data.syncedAt) this.lastSyncAt = data.syncedAt

    // Merge into IndexedDB (upsert — delta updates only touch changed rows)
    await Promise.all(incoming.map((c: ChatConversation) =>
      ChatDatabaseService.saveConversation({ ...c, unread: c.unread ?? 0 })
    ))

    // For delta calls, merge incoming into existing array; for full calls, replace
    if (!forceFull && this.lastSyncAt && incoming.length > 0) {
      // Caller will merge — return only the delta
      return incoming
    }
    return incoming
  }

  /** Reset sync state (e.g. on logout) */
  static resetSync (): void { this.lastSyncAt = null }

  /** Vollständiger Abzug der Konversationen OHNE den Delta-Cursor zu bewegen.
   *  Für Dauerverbraucher wie den Header-Badge (communication store): würden
   *  sie `fetchConversations` benutzen, schöben sie `lastSyncAt` vor und die
   *  offene Chat-Ansicht bekäme beim nächsten Delta die inzwischen geänderten
   *  Konversationen nicht mehr — ihre Liste bliebe stehen. */
  static async fetchConversationsSnapshot (): Promise<ChatConversation[]> {
    const data = await socketRequest('chat:conversations:fetch', { since: null },
      () => apiFetch('/api/chat/conversations'))
    const incoming: ChatConversation[] = data.conversations || []
    // IDB im Hintergrund mitpflegen (der serverseitig gerechnete unread-Stand
    // ist die Wahrheit), aber NICHT darauf warten: ein blockiertes IndexedDB
    // — etwa weil ein zweiter Tab eine Versionsmigration offen hält — würde
    // sonst den fertig geladenen Zählstand aufhalten.
    void Promise.all(incoming.map((c: ChatConversation) =>
      ChatDatabaseService.saveConversation({ ...c, unread: c.unread ?? 0 })
    )).catch(() => {})
    return incoming
  }

  static async createConversation (params: NewConversationParams): Promise<{ conversationId: string }> {
    return apiFetch('/api/chat/conversations', { method: 'POST', body: JSON.stringify(params) })
  }

  static joinConversation (conversationId: string): void {
    socket?.emit('chat:join', { conversationId })
  }

  static onConversationNew (cb: (conv: { conversationId: string; type: string; name: string }) => void): void {
    socket?.on('chat:conversation:new', cb)
  }

  // ── AES Key management (Option B) ────────────────────────────────────────────
  /**
   * Get the decrypted AES conversation key for a given conversation.
   * Fetches the encrypted key from server_chat, decrypts it with this device's
   * RSA private key (stored in ChatDatabaseService), caches result in memory.
   */
  static async getConversationKey (conversationId: string, deviceId: string): Promise<CryptoKey | null> {
    if (aesKeyCache.has(conversationId)) return aesKeyCache.get(conversationId)!

    try {
      const keyPair = await ChatDatabaseService.getMyKeyPair(deviceId)
      if (!keyPair) return null

      const data = await socketRequest('chat:key:fetch', { conversationId },
        () => apiFetch(`/api/chat/keys/conversation/${conversationId}`))
      if (!data?.key?.encrypted_key) return null
      const aesKey = await ChatDatabaseService.unwrapConversationKey(data.key.encrypted_key, keyPair.privateKey)
      aesKeyCache.set(conversationId, aesKey)
      return aesKey
    } catch {
      return null   // 404 = no key for this conversation yet; fall back to plaintext path
    }
  }

  /** Generate a new AES-256-GCM key (used when creating a new conversation) */
  static async generateConversationKey (): Promise<CryptoKey> {
    return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt'])
  }

  /**
   * Wrap (encrypt) an AES key with a device's RSA-OAEP public key.
   * Returns Base64 ciphertext ready for chat_conversation_keys table.
   */
  static async wrapKeyForDevice (aesKey: CryptoKey, devicePublicKeyB64: string): Promise<{ encrypted_key: string; iv: string; tag: string }> {
    const spki    = Uint8Array.from(atob(devicePublicKeyB64), c => c.charCodeAt(0))
    const pubKey  = await crypto.subtle.importKey('spki', spki, { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt'])
    const raw     = await crypto.subtle.exportKey('raw', aesKey)
    const ct      = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, pubKey, raw)
    return {
      encrypted_key: btoa(String.fromCharCode(...new Uint8Array(ct))),
      iv:  '',   // not used for RSA-OAEP (deterministic padding)
      tag: ''
    }
  }

  // ── Messages ─────────────────────────────────────────────────────────────────
  /**
   * Encrypt and send a message.
   * 1. Encrypts plaintext with the conversation AES key (client-side)
   * 2. Emits the ciphertext via socket — server never sees plaintext
   * 3. Saves decrypted copy to local IndexedDB
   */
  static sendMessage (params: SendMessageParams, ownUserId: string): Promise<{ ok: boolean; id?: string }> {
    const { conversationId, plaintext, aesKey, content_type, ref_type, ref_id, ref_label, ref_data, metadata } = params
    return new Promise(async (resolve, reject) => {
      try {
        let body: string, body_iv: string, body_tag: string
        if (aesKey) {
          const enc = await ChatDatabaseService.encryptMessage(plaintext, aesKey)
          body = enc.body; body_iv = enc.body_iv; body_tag = enc.body_tag
        } else {
          // No key available — send plaintext (unencrypted fallback)
          body = plaintext; body_iv = ''; body_tag = ''
        }

        if (!socket) {
          console.warn('[ChatServer] sendMessage: socket not initialised — message not sent')
          resolve({ ok: false })
          return
        }

        // socket.timeout(N).emit — ack callback receives (err, ack):
        // err is a TimeoutError when the server never acknowledges within N ms.
        // Without a timeout the Promise hangs forever when the socket is disconnected
        // (Socket.IO buffers the emit but AUTH_INVALID prevents reconnection → no ack).
        socket.timeout(8000).emit('chat:message', {
          conversationId, body, body_iv, body_tag,
          content_type: content_type || 'text',
          ref_type, ref_id, ref_label, ref_data: ref_data ? JSON.stringify(ref_data) : null,
          metadata: metadata ? JSON.stringify(metadata) : null
        }, async (err: Error | null, ack: { ok: boolean; id?: string }) => {
          if (err) {
            console.warn('[ChatServer] sendMessage: ack timeout or socket error —', err.message)
            resolve({ ok: false })
            return
          }
          if (ack?.ok && ack.id) {
            await ChatDatabaseService.saveMessage({
              id: ack.id, conversationId, senderid: ownUserId, sendertype: 'user',
              content_type: content_type || 'text', body: plaintext,
              ref_type, ref_id, ref_label, ref_data, metadata, status: 'sent',
              created: new Date().toISOString()
            })
          }
          resolve(ack ?? { ok: false })
        })
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Fetch paginated history from server_chat, decrypt each message,
   * and upsert into local IndexedDB.
   */
  static async fetchHistory (conversationId: string, deviceId: string, before?: string): Promise<DecryptedMessage[]> {
    const key = await this.getConversationKey(conversationId, deviceId)
    // No early return — always load history; decrypt if key available, else pass body through

    const data = await socketRequest('chat:history:fetch', { conversationId, before }, () => {
      const qs = before ? `?before=${before}` : ''
      return apiFetch(`/api/chat/messages/${conversationId}${qs}`)
    })
    const msgs: DecryptedMessage[] = []

    for (const m of data.messages || []) {
      if (m.content_type === 'system' || m.content_type === 'form_data' || !m.body_iv) {
        // System messages, form data, or plaintext fallback (no iv) — store body as-is
        msgs.push({ ...m, body: m.body, conversationId })
        await ChatDatabaseService.saveMessage({ ...m, body: m.body, conversationId })
      } else if (key) {
        try {
          const plaintext = await ChatDatabaseService.decryptMessage(m.body, m.body_iv, m.body_tag, key)
          const decrypted = { ...m, body: plaintext, conversationId }
          msgs.push(decrypted)
          await ChatDatabaseService.saveMessage(decrypted)
        } catch {
          msgs.push({ ...m, body: '[Entschlüsselungsfehler]', conversationId })
        }
      } else {
        // Encrypted message but no key available — show placeholder
        msgs.push({ ...m, body: '🔒 Verschlüsselte Nachricht', conversationId })
      }
    }
    return msgs
  }

  static markRead (conversationId: string, messageId: string): void {
    socket?.emit('chat:read', { conversationId, messageId })
    // Header-Badge (communication store) sofort aktualisieren statt auf den 30-s-Poll zu warten —
    // erst nach dem DB-Write, sonst liest der Refresh noch den alten Unread-Stand
    ChatDatabaseService.markRead(conversationId).then(() =>
      window.dispatchEvent(new CustomEvent('chat:unread-changed', {
        detail: { conversationId, read: true }
      }))
    )
  }

  /** Decrypt an incoming real-time message and save to IndexedDB */
  static async handleIncomingMessage (
    msg: any, deviceId: string, currentUserId: string
  ): Promise<DecryptedMessage | null> {
    // Klartext-Nachrichten (kein `body_iv`) gibt es in anonymen Konversationen
    // immer: der Website-Besucher hat kein Gerät und damit keinen Schlüssel.
    // Ohne diesen Zweig verlangte die Methode einen Konversationsschlüssel, den
    // es dort nie gibt, und verwarf jede eintreffende Anfrage-Nachricht
    // stillschweigend — dieselbe Regel wendet fetchHistory schon an.
    const isPlain = !msg.body_iv || msg.content_type === 'form_data' || msg.content_type === 'system'

    let plaintext = msg.body
    if (!isPlain) {
      const aesKey = await this.getConversationKey(msg.conversationId, deviceId)
      if (!aesKey) return null
      plaintext = '[Entschlüsselungsfehler]'
      try {
        plaintext = await ChatDatabaseService.decryptMessage(msg.body, msg.body_iv, msg.body_tag, aesKey)
      } catch {}
    }

    const decrypted: DecryptedMessage = {
      ...msg, body: plaintext,
      conversationId: msg.conversationId || msg.conversationid
    }
    const ts = decrypted.created ?? new Date().toISOString()
    await Promise.all([
      ChatDatabaseService.saveMessage(decrypted),
      ChatDatabaseService.updateConversationPreview(decrypted.conversationId, plaintext.slice(0, 80), ts),
      msg.senderid !== currentUserId
        ? ChatDatabaseService.incrementUnread(decrypted.conversationId)
        : Promise.resolve()
    ])
    return decrypted
  }

  // ── Typing ───────────────────────────────────────────────────────────────────
  static sendTyping (conversationId: string, isTyping: boolean): void {
    socket?.emit('chat:typing', { conversationId, isTyping })
  }

  static onTyping (cb: (data: TypingEvent) => void): void {
    socket?.on('chat:typing:update', cb)
  }

  // ── Keys (device public key management) ──────────────────────────────────────
  /**
   * Sorgt dafür, dass dieses Gerät ein lokales RSA-Schlüsselpaar hat UND der
   * Server genau dessen öffentlichen Schlüssel kennt. Läuft bei jedem Connect.
   *
   * Früher wurde nur hochgeladen, wenn lokal noch kein Paar existierte. Zwei
   * Wege führten so zu einem Gerät, für das neue Konversationen dauerhaft
   * "🔒 Verschlüsselte Nachricht" zeigen:
   *  - Upload schlug einmal fehl (Token abgelaufen, offline) → chat_public_key
   *    blieb NULL, das Gerät wurde beim Erstellen von Konversationen übergangen.
   *  - IndexedDB war kurz nicht offen (Safari: blocked/Fehler → getDB() null) →
   *    getMyKeyPair lieferte null, ein neues Paar wurde erzeugt, NICHT
   *    gespeichert, sein öffentlicher Schlüssel aber hochgeladen. Danach
   *    wrappten Absender für einen Schlüssel, dessen privater Teil weg ist.
   */
  static async ensureDeviceKey (deviceId: string, userId: string): Promise<void> {
    if (!deviceId || !userId) return
    if (!(await ChatDatabaseService.isAvailable())) return
    try {
      let pair = await ChatDatabaseService.getMyKeyPair(deviceId)
      if (!pair) {
        await ChatDatabaseService.generateAndSaveKeyPair(deviceId)
        pair = await ChatDatabaseService.getMyKeyPair(deviceId)
        if (!pair) return
      }
      const devices = await this.fetchDeviceKeys(userId)
      const own = devices.find(d => d.deviceId === deviceId)
      if (own?.publicKey !== pair.publicKeyB64) {
        await this.uploadPublicKey(pair.publicKeyB64)
        console.warn(`[ChatServer] public key of device ${deviceId} was ${own ? 'stale' : 'missing'} on the server — re-uploaded`)
      }
    } catch (err) {
      console.error('[ChatServer] ensureDeviceKey failed', err)
    }
  }

  static async uploadPublicKey (publicKeyB64: string): Promise<void> {
    await apiFetch('/api/chat/keys/device', {
      method: 'POST', body: JSON.stringify({ publicKey: publicKeyB64 })
    })
  }

  static async fetchDeviceKeys (userId: string): Promise<DeviceKey[]> {
    const data = await apiFetch(`/api/chat/keys/device/${userId}`)
    await Promise.all((data.devices || []).map((d: DeviceKey) =>
      ChatDatabaseService.saveDeviceKey(d)
    ))
    return data.devices || []
  }

  // ── New device key transfer ───────────────────────────────────────────────────
  /**
   * Wire up both sides of the new-device key transfer.
   * Responder: when a device lacks conversation keys, re-wrap every requested
   * conversation key this device can decrypt for that device's public key and
   * relay them via the server (blind relay — server never sees plain AES keys).
   * Requests come from this user's other devices AND from the other participants
   * of shared conversations — a user with only one device (trainer on an iPad)
   * otherwise had nobody to ask. Participants hold the key anyway, so the trust
   * model is unchanged; the server checks both sides are participants.
   * Receiver: when a trusted device has stored keys for THIS device, clear the
   * key cache and let the caller reload the open conversation.
   * Call after the socket is connected; safe to call again on reconnect.
   */
  static setupKeySync (deviceId: string, onKeysReceived?: () => void): void {
    if (!socket) return
    socket.off('chat:device:key_request')
    socket.off('chat:device:keys_ready')

    socket.on('chat:device:key_request', async ({ newDeviceId, newDevicePubKey, conversationIds }:
      { requestingUserId: string; newDeviceId: string; newDevicePubKey: string; conversationIds?: string[] }) => {
      if (!newDeviceId || !newDevicePubKey || newDeviceId === deviceId) return
      try {
        const keyPair = await ChatDatabaseService.getMyKeyPair(deviceId)
        if (!keyPair) return
        // anonymous chats are plaintext — no key
        const wanted = Array.isArray(conversationIds) && conversationIds.length
          ? conversationIds
          : (await ChatDatabaseService.getConversations()).filter(c => c.type !== 'anonymous').map(c => c.id)
        const conversationKeys: Array<{ conversationId: string; encrypted_key: string; iv: string; tag: string }> = []
        for (const convId of wanted) {
          try {
            const data = await socketRequest('chat:key:fetch', { conversationId: convId },
              () => apiFetch(`/api/chat/keys/conversation/${convId}`))
            if (!data?.key?.encrypted_key) continue
            // extractable=true: wrapKeyForDevice must export the raw AES key.
            // Deliberately NOT cached — the in-memory cache keeps only non-extractable keys.
            const aesKey  = await ChatDatabaseService.unwrapConversationKey(data.key.encrypted_key, keyPair.privateKey, true)
            const wrapped = await this.wrapKeyForDevice(aesKey, newDevicePubKey)
            conversationKeys.push({ conversationId: convId, ...wrapped })
          } catch { /* no key for this conversation on this device — skip */ }
        }
        if (conversationKeys.length) {
          socket?.emit('chat:device:key_transfer', { newDeviceId, conversationKeys })
          console.log(`[ChatServer] re-wrapped ${conversationKeys.length} conversation keys for device ${newDeviceId}`)
        }
      } catch (err) {
        console.error('[ChatServer] key transfer (responder) failed', err)
      }
    })

    socket.on('chat:device:keys_ready', ({ deviceId: readyDeviceId, count }: { deviceId: string; count: number }) => {
      if (readyDeviceId !== deviceId) return
      console.log(`[ChatServer] received ${count} conversation keys from another device or participant`)
      aesKeyCache.clear()
      onKeysReceived?.()
    })
  }

  /**
   * Check whether this device is missing keys for any encrypted conversation and,
   * if so, ask for them (chat:device:key_request). "Missing" includes a key row
   * this device cannot unwrap (wrapped for a stale public key) — getConversationKey
   * returns null for both. The server relays to this user's other devices and to
   * the other participants of exactly these conversations.
   * No-op when every key is present or nobody is online to answer —
   * callers invoke this on every connect, so it retries naturally.
   */
  static async requestMissingKeys (deviceId: string): Promise<void> {
    try {
      const keyPair = await ChatDatabaseService.getMyKeyPair(deviceId)
      if (!keyPair?.publicKeyB64) return
      const convos = await ChatDatabaseService.getConversations()
      const conversationIds: string[] = []
      for (const conv of convos) {
        if (conv.type === 'anonymous') continue
        if (!(await this.getConversationKey(conv.id, deviceId))) conversationIds.push(conv.id)
      }
      if (conversationIds.length) {
        socket?.emit('chat:device:key_request', {
          newDeviceId: deviceId, newDevicePubKey: keyPair.publicKeyB64, conversationIds
        })
        console.log(`[ChatServer] missing keys for ${conversationIds.length} conversations — requested transfer`)
      }
    } catch (err) {
      console.error('[ChatServer] requestMissingKeys failed', err)
    }
  }

  /**
   * Ordnet einer Anfrage einen Kunden zu (oder löst die Zuordnung mit null).
   * `clientId` ist eine clients_mapping.id — dieselbe Kennung, an der auch
   * Verträge und Buchungen im Studio hängen.
   */
  static async assignClient (conversationId: string, clientId: string | null): Promise<void> {
    await apiFetch(`/api/chat/conversations/${conversationId}/client`, {
      method: 'PUT',
      body: JSON.stringify({ clientId })
    })
  }

  /**
   * Markiert eine Anfrage als erledigt (status 'done') oder nimmt die
   * Markierung zurück (null). Liegt wie die Kundenzuordnung in
   * chat_conversations.options — sichtbar für alle Teilnehmer.
   */
  static async setStatus (conversationId: string, status: 'done' | null): Promise<void> {
    await apiFetch(`/api/chat/conversations/${conversationId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    })
    // Erledigte Anfragen zählen im Header-Badge nicht mehr als offen —
    // sofort melden statt auf den nächsten Sync zu warten.
    window.dispatchEvent(new CustomEvent('chat:unread-changed', {
      detail: { conversationId, status }
    }))
  }

  static onConversationStatus (cb: (data: { conversationId: string; status: string | null }) => void): void {
    socket?.off('chat:conversation:status')
    socket?.on('chat:conversation:status', cb)
  }

  // ── Policy ───────────────────────────────────────────────────────────────────
  static async fetchPolicy (companyId: string): Promise<any> {
    const data = await apiFetch(`/api/chat/policy/${companyId}`)
    return data.policy
  }

  static async savePolicy (companyId: string, policy: Record<string, any>): Promise<void> {
    await apiFetch(`/api/chat/policy/${companyId}`, { method: 'PUT', body: JSON.stringify(policy) })
  }

  // ── Real-time subscriptions ───────────────────────────────────────────────────
  static onMessage (cb: (msg: any) => void): void {
    socket?.on('chat:message:new', cb)
  }

  static onAnonMessage (cb: (msg: any) => void): void {
    socket?.on('chat:anon:new', cb)
  }

  /** Leichtgewichtiges Server-Signal „neue Nachricht in einer deiner Konversationen".
   *  Geht in den user:-Room, kommt also — anders als chat:message:new — auch ohne
   *  gejointe conv:-Rooms an. Grundlage des Ungelesen-Badges im Studio-Header. */
  static onNotify (cb: (data: { conversationId: string }) => void): void {
    socket?.off('chat:notify')
    socket?.on('chat:notify', cb)
  }

  static onPresence (cb: (data: PresenceEvent) => void): void {
    // Server emits 'chat:presence' on connect/disconnect
    socket?.off('chat:presence')
    socket?.on('chat:presence', cb)
  }

  /** Called once on connection — server sends snapshot of all currently online userids */
  static onPresenceSnapshot (cb: (onlineUserIds: string[]) => void): void {
    socket?.off('chat:presence:snapshot')
    socket?.on('chat:presence:snapshot', ({ onlineUserIds }: { onlineUserIds: string[] }) => cb(onlineUserIds))
  }

  /**
   * Aktuellen Presence-Snapshot vom Server abholen.
   * Nötig, weil der Socket global beim Login verbindet (Unread-Badge im
   * Communication-Store) — den Connect-Snapshot verschickt der Server, bevor
   * eine Chat-Ansicht gemountet ist und ihren Listener registriert hat. Ohne
   * aktiven Abruf starten die Views mit leerem onlineUsers und zeigen alle
   * Teilnehmer offline, bis zufällig ein Live-Broadcast eintrifft.
   */
  static fetchPresenceSnapshot (): Promise<string[]> {
    return new Promise((resolve) => {
      if (!socket?.connected) return resolve([])
      socket.timeout(5000).emit('chat:presence:get', (err: any, res: any) => {
        resolve(err ? [] : (res?.onlineUserIds || []))
      })
    })
  }

  static onReadReceipt (cb: (receipt: ReadReceipt) => void): void {
    socket?.on('chat:read:update', cb)
  }

  static off (event: string): void {
    socket?.off(event)
  }

  // ── Drafts ───────────────────────────────────────────────────────────────────
  static async saveDraft (conversationId: string, text: string): Promise<void> {
    await ChatDatabaseService.saveDraft(conversationId, text)
  }

  static async getDraft (conversationId: string): Promise<string> {
    return ChatDatabaseService.getDraft(conversationId)
  }

  // ── Unread total (for header badge) ──────────────────────────────────────────
  static async getUnreadTotal (): Promise<number> {
    const convos = await ChatDatabaseService.getConversations()
    return convos.reduce((sum, c) => sum + (c.unread || 0), 0)
  }
}
